import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { SectionCard } from "../../../../components/design-system/admin";
import { useVendorsApi } from "../../../../common/hooks/use-vendors-api.hook";
import { loadPickerVendors, VendorCombobox } from "./vendor-combobox";

/**
 * Every vendor, not the API's default first page of 20 — with more vendors
 * than that, the rest could never be picked. Same ask as the demo-sites page.
 */
export const VENDOR_PICKER_PAGE = 1;
export const VENDOR_PICKER_LIMIT = 1000;

const ROLES = ["SUPER_ADMIN", "VENDOR_ADMIN"];

export const UserGeneralInformation = ({ user, setUser, check }) => {
    const { t } = useTranslation();
    const [vendors, setVendors] = useState([]);
    const vendorsAPI = useVendorsApi();

    // Which fields the admin has touched: an emptied field shows as invalid
    // before the first save, the rest only once "check" is on.
    const [fields, setFields] = useState({
        firstname: { isTouched: false, name: "firstname" },
        lastname: { isTouched: false, name: "lastname" },
        email: { isTouched: false, name: "email" },
        user: { isTouched: false, name: "user" },
        vendor: { isTouched: false, name: "vendor" },
        role: { isTouched: false, name: "roles" },
    });

    const initVendors = async () => {
        try {
            setVendors(await loadPickerVendors(vendorsAPI, VENDOR_PICKER_PAGE, VENDOR_PICKER_LIMIT));
        } catch (error) {
            console.error("Error loading vendors:", error);
            setVendors([]);
        }
    };

    useEffect(() => {
        initVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFields({
            ...fields,
            [name]: {
                ...fields[name],
                isTouched: true,
            },
        });
        setUser({ ...user, [name]: value });
    };

    const handleVendorChange = (event) => {
        const { name, value } = event.target;
        setFields({
            ...fields,
            [name]: {
                ...fields[name],
                isTouched: true,
            },
        });
        setUser({ ...user, [name]: vendors.find((vendor) => vendor._id === value) });
    };

    const handleRoleChange = (event) => {
        const { name, value } = event.target;
        setFields({
            ...fields,
            [name]: {
                ...fields[name],
                isTouched: true,
            },
        });
        setUser({ ...user, [name]: [value] });
    };

    const handleToggleEnabled = (checked: boolean) => {
        setUser({ ...user, isEnabled: checked });
    };

    const isValidInput = (name) => {
        return (check && !user?.[name]) || (fields && fields[name] && fields[name].isTouched && !user?.[name]);
    };

    const roleLabel = (role: string) => t(`ADMIN_USERS.ROLES.${role}`);
    const enabled = user?.isEnabled !== false;

    return (
        <SectionCard title={t("ADMIN_USERS.FORM.GENERAL_TITLE")} description={t("ADMIN_USERS.FORM.GENERAL_DESCRIPTION")}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="user-firstname" className="text-xs text-muted-foreground">
                        {t("ADMIN_USERS.FORM.FIRST_NAME")} *
                    </Label>
                    <Input
                        id="user-firstname"
                        aria-invalid={isValidInput(fields.firstname.name) || undefined}
                        name={fields.firstname.name}
                        placeholder={t("ADMIN_USERS.FORM.FIRST_NAME_PLACEHOLDER")}
                        value={user?.firstname || ""}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="user-lastname" className="text-xs text-muted-foreground">
                        {t("ADMIN_USERS.FORM.LAST_NAME")} *
                    </Label>
                    <Input
                        id="user-lastname"
                        aria-invalid={isValidInput(fields.lastname.name) || undefined}
                        name={fields.lastname.name}
                        placeholder={t("ADMIN_USERS.FORM.LAST_NAME_PLACEHOLDER")}
                        value={user?.lastname || ""}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="user-email" className="text-xs text-muted-foreground">
                        {t("ADMIN_USERS.FORM.EMAIL")} *
                    </Label>
                    <Input
                        id="user-email"
                        aria-invalid={isValidInput(fields.email.name) || undefined}
                        name={fields.email.name}
                        placeholder={t("ADMIN_USERS.FORM.EMAIL_PLACEHOLDER")}
                        value={user?.email || ""}
                        onChange={handleChange}
                        type="email"
                        autoComplete="off"
                        required
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="user-role" className="text-xs text-muted-foreground">
                        {t("ADMIN_USERS.FORM.ROLE")} *
                    </Label>
                    <Select
                        value={user?.roles ? user.roles[0] : "VENDOR_ADMIN"}
                        onValueChange={(value) =>
                            handleRoleChange({ target: { name: fields.role.name, value } })
                        }
                    >
                        <SelectTrigger
                            id="user-role"
                            className="w-full"
                            aria-invalid={isValidInput(fields.role.name) || undefined}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ROLES.map((role) => (
                                <SelectItem value={role} key={role}>
                                    {roleLabel(role)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t("ADMIN_USERS.FORM.ROLE_HINT")}</p>
                </div>

                {user && user.roles && user.roles[0] === "VENDOR_ADMIN" && (
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="user-vendor" className="text-xs text-muted-foreground">
                            {t("ADMIN_USERS.FORM.VENDOR")} *
                        </Label>
                        <VendorCombobox
                            id="user-vendor"
                            vendors={vendors}
                            value={user?.vendor?._id || ""}
                            selected={user?.vendor}
                            onChange={(value) => handleVendorChange({ target: { name: fields.vendor.name, value } })}
                            invalid={isValidInput(fields.vendor.name) || undefined}
                            placeholder={t("ADMIN_USERS.FORM.VENDOR_PLACEHOLDER")}
                            searchPlaceholder={t("ADMIN_USERS.FORM.VENDOR_SEARCH")}
                            emptyText={t("ADMIN_USERS.FORM.VENDOR_EMPTY")}
                        />
                    </div>
                )}

                <div className="flex items-start justify-between gap-4 rounded-lg border p-4 sm:col-span-2">
                    <div className="flex flex-col gap-0.5">
                        <Label htmlFor="user-enabled" className="text-sm font-medium">
                            {t("ADMIN_USERS.FORM.STATUS")}
                        </Label>
                        <span className="text-xs text-muted-foreground">
                            {enabled ? t("ADMIN_USERS.FORM.STATUS_ENABLED") : t("ADMIN_USERS.FORM.STATUS_DISABLED")}
                        </span>
                    </div>
                    <Switch id="user-enabled" checked={enabled} onCheckedChange={handleToggleEnabled} />
                </div>
            </div>
        </SectionCard>
    );
};
